import { useState, useEffect, useRef, Suspense } from 'react'
import Modal from '.'
import PopupModal from './PopupModal'
import { connect } from 'react-redux'
import { getNominative } from '../../common/utils'
import Field from '../fields'
import { history } from '../../store'
import { FormWarning } from '../forms/FormWarning'
import styled from 'styled-components'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { Gender } from '@lmc/ts-common'
import { GenderIdentityEnum, SexEnum } from '@lmc/validations'
import { GENDER_IDENTITY_OPTIONS, SEX_OPTIONS } from 'common/constants'

interface ResidentData {
  status: string
  name: {
    first: string
    last: string
  }
  gender: string
  demographics?: {
    genderIdentity?: string
    sex?: string
  }
  dateOfBirth: string
  dols: boolean
  dnacpr: boolean
  fundingSource: string
}

interface AddResidentModalProps {
  createItem: (data: any) => void
  fetchList: (list: string) => void
  showToast: (options: { title: string }) => void
  features: any
  fields: ResidentData
  setFormField: (data: any) => void
  residents: any[]
  carePlans: any
  preferredPeopleNominative: string
  isOpen: boolean
}

export const AddResidentModal = (props: AddResidentModalProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const [statusStep, setStatusStep] = useState(true)
  const [showWarning, setShowWarning] = useState(false)
  const initialized = useRef(false)
  const featureFlags = useFeatureFlags({
    flagNames: ['mods_demographics']
  })?.data
  const isModsDemographicsEnabled = featureFlags?.mods_demographics?.enabled

  useEffect(() => {
    const { features, fields, setFormField } = props
    if (features && fields && !initialized.current) {
      setFormField({
        key: 'status',
        value: 'active'
      })
      initialized.current = true
    }
  }, [props.features, props.fields, props.setFormField])

  useEffect(() => {
    const { fields, residents } = props
    const firstNameAndLastNameProvided =
      fields?.name?.first && fields?.name?.last
    if (firstNameAndLastNameProvided) {
      ;(residents || []).find(
        (r) =>
          r.name.first === fields.name.first && r.name.last === fields.name.last
      )
        ? setShowWarning(true)
        : setShowWarning(false)
    } else {
      setShowWarning(false)
    }
  }, [props.fields?.name])

  const handleSubmit = (options: any) => {
    const { fields, setFormField, residents } = props
    const hasOneDummy =
      residents?.filter((item) => item?.status === 'dummy').length === 1
    const existingDummy = residents?.find((item) => item?.status === 'dummy')
    const existingDummyIsMale = isModsDemographicsEnabled
      ? existingDummy?.demographics?.genderIdentity === GenderIdentityEnum.male
      : existingDummy?.gender === Gender.male
    const dummyResident =
      dummyNames[hasOneDummy && existingDummyIsMale ? 1 : 0]
    const isStatusStep = statusStep && options.length > 1
    if (fields?.status === 'dummy') {
      if (!fields?.name?.first) {
        setFormField({
          key: 'name.first',
          value: dummyResident.firstName
        })
      }
      if (!fields?.name?.last) {
        setFormField({
          key: 'name.last',
          value: dummyResident.lastName
        })
      }
      if (isModsDemographicsEnabled) {
        if (!fields?.demographics?.genderIdentity) {
          setFormField({
            key: 'demographics.genderIdentity',
            value: dummyResident.genderIdentity
          })
        }
        if (!fields?.demographics?.sex) {
          setFormField({
            key: 'demographics.sex',
            value: dummyResident.sex
          })
        }
      } else {
        if (!fields?.gender) {
          setFormField({
            key: 'gender',
            value: dummyResident.gender
          })
        }
      }
      if (!fields?.dateOfBirth) {
        setFormField({
          key: 'dateOfBirth',
          value: dummyResident.dateOfBirth
        })
      }
      if (!fields?.dols) {
        setFormField({
          key: 'dols',
          value: dummyResident.dols
        })
      }
      if (!fields?.dnacpr) {
        setFormField({
          key: 'dnacpr',
          value: dummyResident.dnacpr
        })
      }
      if (!fields?.fundingSource) {
        setFormField({
          key: 'fundingSource',
          value: dummyResident.fundingSource
        })
      }
    }
    setStatusStep(false)
    if (!isStatusStep) {
      props.createItem({
        url: 'list/residents',
        onComplete: handleComplete
      })
    }
  }

  const handleComplete = (resident: any) => {
    const { fetchList, showToast, preferredPeopleNominative } = props
    fetchList('residents')
    history.push(`/residents/${resident.id}/profile`)
    setStatusStep(true)
    showToast({
      title: `Added a ${getNominative(preferredPeopleNominative, 'Resident')}`
    })
  }

  const {
    fields,
    isOpen,
    carePlans,
    features,
    preferredPeopleNominative,
    residents,
    ...formProps
  } = props
  const dummysAllowed =
    residents?.filter((item) => item?.status === 'dummy').length <= 1
  const isDummy = fields?.status === 'dummy'
  const nominative = getNominative(
    preferredPeopleNominative,
    'service user'
  ).toLowerCase()
  const options = [
    {
      label: 'Active',
      description: `Create a profile for a ${nominative}. Note: this will impact your bill.`,
      value: 'active'
    }
  ]
  if (carePlans) {
    options.push({
      label: 'Initial Assessment',
      description: `Create a profile for a potential ${nominative} to assess their care needs.`,
      value: 'pre-assessment'
    })
  }
  if (dummysAllowed) {
    options.push({
      label: 'Dummy Profile',
      description:
        'Create a fake profile to explore Log my Care without affecting your bill. ',
      value: 'dummy'
    })
  }
  if (!isOpen || !fields) return null
  const isStatusStep = statusStep && options.length > 1
  return (
    <PopupModal
      confirmText={isStatusStep ? 'Next' : 'Add'}
      buttonIcon={isStatusStep ? undefined : 'PlusOutlined'}
      buttonColour="success"
      list={getNominative(preferredPeopleNominative, 'Resident')}
      includesRequired={!isStatusStep}
      onConfirm={() => handleSubmit(options)}
      onClose={() => setStatusStep(true)}
      titleDescription={isStatusStep ? 'Set status' : undefined}
      type="create"
      {...formProps}
    >
      {isStatusStep ? (
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            formId={MODAL_ID}
            keys="status"
            label="Current Status"
            options={options}
            supportLink={SUPPORT_LINK}
            required
            type="radios"
            values={fields}
            floatHelpLink
            withDescription
          />
        </Suspense>
      ) : (
        <>
          <div style={styles.fieldWrapper}>
            <Suspense fallback={<div>Loading...</div>}>
              <Field
                formId={MODAL_ID}
                keys={['name.first', 'name.last']}
                label="Name"
                placeholders={['First name', 'Last name']}
                required
                type="multiText"
                values={fields}
                disabled={isDummy}
                layout="column"
              />
            </Suspense>
          </div>

          {showWarning && (
            <FormWarningWrapper>
              <FormWarning message="An individual with this name may already exist. Check before adding or use a preferred name" />
            </FormWarningWrapper>
          )}

          <div style={styles.fieldWrapper}>
            <Suspense fallback={<div>Loading...</div>}>
              <Field
                formId={MODAL_ID}
                keys="preferredName"
                label="Preferred name (optional)"
                type="text"
                values={fields}
                disabled={isDummy}
                layout="column"
                prefillPreferredName={false}
              />
            </Suspense>
          </div>
          {isModsDemographicsEnabled ? (
            <>
              <div style={styles.fieldWrapper}>
                <Suspense fallback={<div>Loading...</div>}>
                  <Field
                    formId={MODAL_ID}
                    keys="demographics.genderIdentity"
                    className="gender-identity-field"
                    label={t(supConstants.GENDER_IDENTITY)}
                    options={GENDER_IDENTITY_OPTIONS}
                    type="antdSelect"
                    values={fields}
                    disabled={isDummy}
                    layout="column"
                    placeholder={t(supConstants.SELECT_GENDER_IDENTITY)}
                    selectStyle={{ width: '100%', height: 40 }}
                  />
                </Suspense>
              </div>
              <div style={styles.fieldWrapper}>
                <Suspense fallback={<div>Loading...</div>}>
                  <Field
                    formId={MODAL_ID}
                    keys="demographics.sex"
                    className="sex-field"
                    label={t(supConstants.SEX)}
                    options={SEX_OPTIONS}
                    type="antdSelect"
                    values={fields}
                    disabled={isDummy}
                    layout="column"
                    placeholder={t(supConstants.SELECT_SEX)}
                    selectStyle={{ width: '100%', height: 40 }}
                  />
                </Suspense>
              </div>
            </>
          ) : (
            <div style={styles.fieldWrapper}>
              <Suspense fallback={<div>Loading...</div>}>
                <Field
                  formId={MODAL_ID}
                  keys="gender"
                  className="gender-field"
                  label="Gender"
                  options={[{ value: 'male' }, { value: 'female' }]}
                  required
                  type="radios"
                  values={fields}
                  disabled={isDummy}
                  layout="column"
                  optionFieldStyle={{
                    border: 'none',
                    justifyContent: 'flex-start',
                    minWidth: 108,
                    paddingLeft: 0
                  }}
                  optionIconStyle={{ order: 1, marginRight: 8 }}
                  optionLabelStyle={{ order: 2 }}
                />
              </Suspense>
            </div>
          )}
          <div style={styles.fieldWrapper}>
            <Suspense fallback={<div>Loading...</div>}>
              <Field
                className="birthdate-field"
                formId={MODAL_ID}
                keys="dateOfBirth"
                label="Date of Birth"
                placeholders="DD-MM-YYYY"
                required
                type="birthdate"
                validationTypes="birthdate"
                values={fields}
                disabled={isDummy}
                layout="column"
              />
            </Suspense>
          </div>
        </>
      )}
    </PopupModal>
  )
}

const MODAL_ID = 'addResident'
const SUPPORT_LINK =
  'https://support.logmycare.co.uk/en/articles/3376538-how-do-i-use-initial-assessments'
const styles = {
  fieldWrapper: {
    marginBottom: 20
  }
}

const mapStateToProps = (state: any) => ({
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  residents: state.data.residents
})

const dummyNames = [
  {
    firstName: 'John',
    lastName: 'Q. Public',
    gender: 'male',
    genderIdentity: GenderIdentityEnum.male,
    sex: SexEnum.male,
    dateOfBirth: '09-08-1982',
    dols: false,
    dnacpr: false,
    fundingSource: 'Local Authority'
  },
  {
    firstName: 'Jane',
    lastName: 'Smith',
    gender: 'female',
    genderIdentity: GenderIdentityEnum.female,
    sex: SexEnum.female,
    dateOfBirth: '24-12-1962',
    dols: false,
    dnacpr: false,
    fundingSource: 'Local Authority'
  }
]

const FormWarningWrapper = styled.div`
  max-width: 360px;
  margin-bottom: 24px;
`

const wrappedModal = connect(mapStateToProps, null)(AddResidentModal)

export default Modal(wrappedModal, MODAL_ID)
